import { supabase } from "@/integrations/supabase/client";
import { Course, CourseLesson, UserCourseProgress, CreateCourseInput } from "@/types/library";
import { resolveCourseToChallengeId } from "@/services/courseIdResolver";
import { userChallengeService } from "@/services/userChallengeService";

export const coursesService = {
  // קבלת כל הקורסים המפורסמים
  async getAllPublishedCourses(): Promise<Course[]> {
    const { data, error } = await supabase
      .from("courses")
      .select("*")
      .eq("is_published", true)
      .order("sort_order", { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // קבלת קורס לפי ID
  async getCourseById(id: string): Promise<Course | null> {
    const { data, error } = await supabase
      .from("courses")
      .select("*")
      .eq("id", id)
      .maybeSingle();

    if (error) throw error;
    return data;
  },

  // קבלת שיעורי קורס
  async getCourseLessons(courseId: string): Promise<CourseLesson[]> {
    const { data, error } = await supabase
      .from("course_lessons")
      .select("*")
      .eq("course_id", courseId)
      .order("sort_order", { ascending: true });

    if (error) throw error;
    return data || [];
  },

  async resolveCourseChallengeId(courseId: string): Promise<string | null> {
    return resolveCourseToChallengeId(courseId);
  },

  // קבלת התקדמות משתמש בקורס — קורא מ-user_challenge_enrollments,
  // מחזיר במבנה הישן למען תאימות לאחור עם הקוד הקיים בספרייה.
  async getCourseProgress(courseId: string, userId?: string): Promise<UserCourseProgress | null> {
    let targetUserId = userId;
    if (!targetUserId) {
      const { data: { user } } = await supabase.auth.getUser();
      targetUserId = user?.id;
    }
    if (!targetUserId) return null;

    const challengeId = await this.resolveCourseChallengeId(courseId);
    if (!challengeId) return null;

    const { data: enrollment, error } = await supabase
      .from("user_challenge_enrollments")
      .select(
        "id, current_lesson_number, completed_lesson_numbers, started_at, last_accessed_at, completed_at"
      )
      .eq("user_id", targetUserId)
      .eq("challenge_id", challengeId)
      .maybeSingle();

    if (error) throw error;
    if (!enrollment) return null;

    const { count: totalLessons } = await supabase
      .from("course_lessons")
      .select("id", { count: "exact", head: true })
      .eq("course_id", courseId);

    const completed: number[] = Array.isArray(enrollment.completed_lesson_numbers)
      ? (enrollment.completed_lesson_numbers as number[])
      : [];
    const total = totalLessons ?? 0;
    const pct = total > 0 ? Math.round((completed.length / total) * 100) : 0;

    return {
      id: enrollment.id,
      user_id: targetUserId,
      course_id: courseId,
      current_lesson: enrollment.current_lesson_number ?? 1,
      completed_lessons: completed,
      progress_percentage: pct,
      started_at: enrollment.started_at ?? new Date().toISOString(),
      last_accessed_at: enrollment.last_accessed_at ?? new Date().toISOString(),
      completed_at: enrollment.completed_at,
    };
  },

  // קבלת כל ההתקדמות של משתמש על פני כל הקורסים
  async getAllUserProgress(userId?: string): Promise<UserCourseProgress[]> {
    let targetUserId = userId;
    if (!targetUserId) {
      const { data: { user } } = await supabase.auth.getUser();
      targetUserId = user?.id;
    }
    if (!targetUserId) return [];

    const { data: enrollments, error } = await supabase
      .from("user_challenge_enrollments")
      .select(
        `id, current_lesson_number, completed_lesson_numbers, started_at, last_accessed_at, completed_at,
         challenge:challenges!inner(id, challenge_type, title, purchase_reference)`
      )
      .eq("user_id", targetUserId)
      .eq("challenge.challenge_type", "course");

    if (error) throw error;
    if (!enrollments?.length) return [];

    // Map each enrollment back to its courses row via title or purchase_reference
    type ChallengeRef = { id: string; challenge_type: string; title: string; purchase_reference: string | null };
    type EnrollmentRow = {
      id: string;
      current_lesson_number: number | null;
      completed_lesson_numbers: number[] | null;
      started_at: string | null;
      last_accessed_at: string | null;
      completed_at: string | null;
      challenge: ChallengeRef;
    };
    const rows = enrollments as unknown as EnrollmentRow[];
    const challenges = rows.map((e) => e.challenge);
    const titles = challenges.map((c) => c.title);
    const refs = challenges.map((c) => c.purchase_reference).filter(Boolean) as string[];

    const { data: courses } = await supabase
      .from("courses")
      .select("id, title, woocommerce_product_id, total_lessons")
      .or(
        [
          `title.in.(${titles.map((t) => `"${t.replace(/"/g, '\\"')}"`).join(",")})`,
          refs.length ? `woocommerce_product_id.in.(${refs.join(",")})` : "",
        ]
          .filter(Boolean)
          .join(",")
      );

    const findCourse = (challenge: ChallengeRef) => {
      if (challenge.purchase_reference) {
        const c = courses?.find(
          (x) => x.woocommerce_product_id === challenge.purchase_reference
        );
        if (c) return c;
      }
      return courses?.find((x) => x.title === challenge.title) || null;
    };

    return rows
      .map((e) => {
        const course = findCourse(e.challenge);
        if (!course) return null;
        const completed: number[] = Array.isArray(e.completed_lesson_numbers)
          ? (e.completed_lesson_numbers as number[])
          : [];
        const total = course.total_lessons ?? 0;
        const pct = total > 0 ? Math.round((completed.length / total) * 100) : 0;
        return {
          id: e.id,
          user_id: targetUserId,
          course_id: course.id,
          current_lesson: e.current_lesson_number ?? 1,
          completed_lessons: completed,
          progress_percentage: pct,
          started_at: e.started_at ?? new Date().toISOString(),
          last_accessed_at: e.last_accessed_at ?? new Date().toISOString(),
          completed_at: e.completed_at,
        } as UserCourseProgress;
      })
      .filter((x): x is UserCourseProgress => x != null);
  },

  // עדכון התקדמות שיעור — מעדכן את user_challenge_enrollments
  async markLessonComplete(courseId: string, lessonNumber: number): Promise<void> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated");

    const challengeId = await this.resolveCourseChallengeId(courseId);
    if (!challengeId) throw new Error("course_not_mapped_to_challenge");

    const { data: existingRow, error: fetchError } = await supabase
      .from("user_challenge_enrollments")
      .select("id, completed_lesson_numbers")
      .eq("user_id", user.id)
      .eq("challenge_id", challengeId)
      .maybeSingle();
    if (fetchError) throw fetchError;

    // Auto-create enrollment if missing — covers users whose access was
    // granted before today's RPC (or whose grant did not auto-enroll for
    // any other reason). The user must already have user_course_access
    // (otherwise the check_course_access trigger will block this insert).
    let enrollment = existingRow;
    if (!enrollment) {
      const { data: created, error: enrollError } = await supabase
        .from("user_challenge_enrollments")
        .insert({
          user_id: user.id,
          challenge_id: challengeId,
          current_day: 1,
          current_lesson_number: 1,
        })
        .select("id, completed_lesson_numbers")
        .single();
      if (enrollError) throw enrollError;
      enrollment = created;
    }

    const existing: number[] = Array.isArray(enrollment.completed_lesson_numbers)
      ? (enrollment.completed_lesson_numbers as number[])
      : [];
    const next = existing.includes(lessonNumber)
      ? existing
      : [...existing, lessonNumber].sort((a, b) => a - b);

    const { error: updateError } = await supabase
      .from("user_challenge_enrollments")
      .update({
        completed_lesson_numbers: next,
        current_lesson_number: lessonNumber + 1,
        last_accessed_at: new Date().toISOString(),
      })
      .eq("id", enrollment.id);
    if (updateError) throw updateError;

    // Check if all lessons are now complete → mark course as completed
    const { count: totalLessonCount } = await supabase
      .from("course_lessons")
      .select("id", { count: "exact", head: true })
      .eq("course_id", courseId);

    if (totalLessonCount && next.length >= totalLessonCount) {
      const { data: challenge } = await supabase
        .from("challenges")
        .select("grace_period_days")
        .eq("id", challengeId)
        .maybeSingle();
      const graceDays = (challenge?.grace_period_days as number | null) || 7;
      await userChallengeService.markCourseAsCompleted(enrollment.id, graceDays);
    }
  },

  // יצירת קורס חדש (admin only) — נוצר כטיוטה, חייב WC product id
  async createCourse(input: CreateCourseInput): Promise<Course> {
    const {
      data: { user },
    } = await supabase.auth.getUser();
    if (!user) throw new Error("User not authenticated");

    if (!input.title?.trim()) throw new Error("title_required");
    if (!input.description?.trim()) throw new Error("description_required");
    if (!input.woocommerce_product_id?.trim())
      throw new Error("woocommerce_product_id_required");
    // total_lessons / estimated_minutes may be 0 at draft creation —
    // they get populated as lessons are added. publishCourse() enforces
    // lesson_count > 0 before flipping is_published.

    const { data, error } = await supabase
      .from("courses")
      .insert({
        ...input,
        is_published: false,
      })
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // פרסום קורס (admin only) — דורש שיש לפחות שיעור אחד
  async publishCourse(courseId: string): Promise<Course> {
    const { count, error: countError } = await supabase
      .from("course_lessons")
      .select("id", { count: "exact", head: true })
      .eq("course_id", courseId);

    if (countError) throw countError;
    if (!count || count <= 0) throw new Error("cannot_publish_course_without_lessons");

    const { data, error } = await supabase
      .from("courses")
      .update({ is_published: true })
      .eq("id", courseId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // עדכון קורס (admin only)
  async updateCourse(id: string, updates: Partial<Course>): Promise<Course> {
    const { data, error } = await supabase
      .from("courses")
      .update(updates)
      .eq("id", id)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  // מחיקת קורס (admin only)
  async deleteCourse(id: string): Promise<void> {
    const { error } = await supabase.from("courses").delete().eq("id", id);

    if (error) throw error;
  },

  // חיפוש קורסים
  async searchCourses(query: string): Promise<Course[]> {
    const { data, error } = await supabase
      .from("courses")
      .select("*")
      .eq("is_published", true)
      .or(`title.ilike.%${query}%,description.ilike.%${query}%`)
      .order("sort_order", { ascending: true });

    if (error) throw error;
    return data || [];
  },

  // ניהול שיעורים — דורש כותרת לא ריקה, lesson_number אוטומטי אם לא סופק
  async createLesson(courseId: string, lesson: Partial<CourseLesson>): Promise<CourseLesson> {
    if (!lesson.title?.trim()) throw new Error("lesson_title_required");

    let lessonNumber = lesson.lesson_number;
    if (!lessonNumber) {
      const { data: maxRow, error: maxError } = await supabase
        .from("course_lessons")
        .select("lesson_number")
        .eq("course_id", courseId)
        .order("lesson_number", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (maxError) throw maxError;
      lessonNumber = (maxRow?.lesson_number ?? 0) + 1;
    }

    const lessonData = {
      course_id: courseId,
      title: lesson.title.trim(),
      lesson_number: lessonNumber,
      description: lesson.description || null,
      video_url: lesson.video_url || null,
      content_text: lesson.content_text || null,
      video_duration: lesson.video_duration || null,
      resources: lesson.resources || null,
      is_preview: lesson.is_preview || false,
      sort_order: lesson.sort_order ?? lessonNumber,
    };

    const { data, error } = await supabase
      .from("course_lessons")
      .insert(lessonData)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async updateLesson(lessonId: string, updates: Partial<CourseLesson>): Promise<CourseLesson> {
    const { data, error } = await supabase
      .from("course_lessons")
      .update(updates)
      .eq("id", lessonId)
      .select()
      .single();

    if (error) throw error;
    return data;
  },

  async deleteLesson(lessonId: string): Promise<void> {
    const { error } = await supabase
      .from("course_lessons")
      .delete()
      .eq("id", lessonId);

    if (error) throw error;
  },

  async reorderLessons(
    courseId: string,
    lessons: { id: string; sort_order: number }[]
  ): Promise<void> {
    const { error } = await supabase.rpc("reorder_lessons", {
      p_course_id: courseId,
      p_orders: lessons,
    });
    if (error) throw error;
  },

  // קבלת כל הקורסים (כולל לא מפורסמים) - לאדמין
  async getAllCourses(): Promise<Course[]> {
    const { data, error } = await supabase
      .from("courses")
      .select("*")
      .order("sort_order", { ascending: true });

    if (error) throw error;
    return data || [];
  },
};
